import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';

const ImageReviewPanel = () => {
    const { currentUser } = usePIM();
    // usePIM usually provides user/token/etc. Checking context file... 
    // PIMContext provides: token is stored in localStorage but context has `user`. 
    // Wait, context provider doesn't seem to pass `token` directly in value? 
    // value={...} is missing in the file view. 
    // I need to see the value prop of PIMProvider.
    // But usually auth token is in localStorage or exposed via context. 
    // Let's assume usePIM() returns everything needed.
    const token = localStorage.getItem('token'); // Fallback if context doesn't expose it.
    const [images, setImages] = useState([]);
    const [stats, setStats] = useState({ total: 0, analyzed: 0 });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(null);
    const [triggeringManual, setTriggeringManual] = useState(false);

    // Use relative path so Nginx proxy handles it (works for both local dev if proxy set up, and production)
    const BACKEND_URL = '';

    const fetchImages = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Fetch stats in parallel
            fetch(`${BACKEND_URL}/api/admin/images/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(r => r.ok && r.json())
                .then(data => data && setStats(data))
                .catch(err => console.error("Failed to load image stats", err));

            const res = await fetch(`${BACKEND_URL}/api/admin/images/pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setImages(data);
            }
        } catch (err) {
            console.error("Failed to load images", err);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // 1. Initial Fetch
    useEffect(() => {
        if (token) fetchImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // 2. Auto-polling for processing items
    useEffect(() => {
        const hasProcessing = images.some(img => img.status === 'processing_vision');
        let interval;
        if (hasProcessing) {
            interval = setInterval(() => {
                fetchImages(true);
            }, 5000);
        }
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images]);

    const handleRunManualBatch = async () => {
        if (!confirm("Chat will be briefly unavailable during high-quality image analysis. Continue?")) return;
        setTriggeringManual(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/vision/analyze-now`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                // Instantly fetch to see 'processing_vision' state
                fetchImages();
            } else {
                alert(data.error || "Failed to trigger analysis.");
            }
        } catch (err) {
            alert("Error: " + err.message);
        } finally {
            setTriggeringManual(false);
        }
    };

    const handleStopManualBatch = async () => {
        if (!confirm("Are you sure you want to stop the image analysis? This will return the analysis models to standby.")) return;
        setTriggeringManual(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/vision/analyze-stop`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                alert(data.message || "Analysis aborted.");
                fetchImages();
            } else {
                alert(data.error || "Failed to abort analysis.");
            }
        } catch (err) {
            alert("Error: " + err.message);
        } finally {
            setTriggeringManual(false);
        }
    };

    const handleConfirm = async (imgId, data) => {
        setSubmitting(imgId);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/images/${imgId}/confirm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });
            if (res.ok) {
                setImages(prev => prev.filter(i => i.id !== imgId));
                // Toast success?
            } else {
                alert("Failed to confirm");
            }
        } catch (err) {
            alert("Error: " + err.message);
        } finally {
            setSubmitting(null);
        }
    };

    const handleReject = async (imgId) => {
        if (!confirm("Reject this image? It will not be searchable.")) return;
        setSubmitting(imgId);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/images/${imgId}/reject`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setImages(prev => prev.filter(i => i.id !== imgId));
            }
        } catch (err) {
            alert("Error: " + err.message);
        } finally {
            setSubmitting(null);
        }
    };

    const handleAnalyzeImage = async (imgId) => {
        if (!confirm("Chat will be briefly unavailable during high-quality image analysis. Continue?")) return;
        setTriggeringManual(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/vision/analyze-now`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ imageId: imgId })
            });
            const data = await res.json();
            if (res.ok) {
                // Immediately set to processing visually
                setImages(prev => prev.map(img => img.id === imgId ? { ...img, status: 'processing_vision' } : img));
                alert("Vision AI Analysis started for this image in the background.");
            } else {
                alert(data.error || "Failed to start analysis");
            }
        } catch (err) {
            alert("Error: " + err.message);
        } finally {
            setTriggeringManual(false);
        }
    };

    return (
        <div className="p-6 h-full flex flex-col bg-[var(--color-bg-content)] text-[var(--color-text-primary)]">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h1 className="text-2xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
                        📷 Image Review & Analysis
                    </h1>

                    {/* Stats Badge */}
                    <div className="flex items-center gap-2 bg-stone-800/50 border border-stone-700 px-3 py-1.5 rounded-full shadow-sm">
                        <span className="text-sm text-stone-300 font-medium">Alle Bilder: <span className="text-white">{stats.total}</span></span>
                        <span className="h-3 w-px bg-stone-600"></span>
                        <span className="text-sm text-blue-300 font-medium">Analysiert: <span className="text-blue-100">{stats.analyzed}</span></span>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={handleRunManualBatch}
                        disabled={triggeringManual || loading || images.some(img => img.status === 'processing_vision')}
                        className={`flex items-center gap-2 px-4 py-2 text-white rounded font-medium shadow-lg transition-colors ${images.some(img => img.status === 'processing_vision')
                                ? 'bg-stone-600 opacity-50 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                        title={images.some(img => img.status === 'processing_vision') ? "Analysis already running" : "Start batch analysis"}
                    >
                        {triggeringManual ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Triggering...
                            </>
                        ) : '🚀 Run Manual Batch Analysis'}
                    </button>
                    <button
                        onClick={handleStopManualBatch}
                        disabled={triggeringManual || loading || !images.some(img => img.status === 'processing_vision')}
                        className={`flex items-center gap-2 px-4 py-2 text-white rounded font-medium shadow-lg transition-colors ${!images.some(img => img.status === 'processing_vision')
                            ? 'bg-stone-600 opacity-50 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                        title={!images.some(img => img.status === 'processing_vision') ? "No analysis running" : "Stop running analysis"}
                    >
                        🛑 Abort Analysis
                    </button>
                </div>
            </div>

            {loading && <p className="text-[var(--color-text-muted)]">Loading pending images...</p>}

            {!loading && images.length === 0 && (
                <div className="bg-[var(--color-bg-panel)] p-8 rounded shadow text-center text-[var(--color-text-muted)] border border-[var(--color-border)]">
                    No pending images found. Good job!
                </div>
            )}

            <div className="space-y-8 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {images.map(img => (
                    <ImageReviewCard
                        key={img.id}
                        img={img}
                        onConfirm={handleConfirm}
                        onReject={handleReject}
                        onAnalyze={handleAnalyzeImage}
                        submitting={submitting === img.id}
                        triggering={triggeringManual}
                        backendUrl={BACKEND_URL}
                    />
                ))}
            </div>
        </div>
    );
};

const ImageReviewCard = ({ img, onConfirm, onReject, onAnalyze, submitting, triggering, backendUrl }) => {
    // Automate: Pre-fill description and title on load
    const [title, setTitle] = useState(img.ai_suggested_title || '');
    const [description, setDescription] = useState(img.ai_suggested_description || '');
    const descRef = React.useRef(null);

    const handleSubmit = () => {
        if (!title.trim()) return;
        onConfirm(img.id, { title, description });
    };

    const handleEdit = () => {
        if (descRef.current) {
            descRef.current.focus();
        }
    };

    const isProcessing = img.status === 'processing_vision';
    const isAwaiting = img.status === 'awaiting_analysis';
    const isReady = img.status === 'pending';

    return (
        <div className="relative bg-[var(--color-bg-panel)] rounded-lg shadow-xl overflow-hidden border border-[var(--color-border)] flex flex-col md:flex-row group hover:border-blue-500/50 transition-colors">
            {/* Processing Overlay */}
            {isProcessing && (
                <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center text-white">
                    <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-xl font-bold tracking-wider animate-pulse">Processing Analysis...</span>
                </div>
            )}

            {/* Image Preview */}
            <div className="w-full md:w-1/3 bg-black/30 p-4 flex items-center justify-center border-r border-[var(--color-border)] relative">
                <a href={`${backendUrl}/uploads/${img.image_ref}`} target="_blank" rel="noopener noreferrer">
                    <img
                        src={`${backendUrl}/uploads/${img.image_ref}`}
                        alt="Context"
                        className="max-h-64 object-contain rounded hover:scale-105 transition-transform cursor-zoom-in shadow-lg"
                    />
                </a>
                {isAwaiting && (
                    <div className="absolute top-2 left-2 bg-yellow-600/80 text-white text-xs px-2 py-1 rounded font-bold shadow">
                        Awaiting Analysis
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className={`flex-1 p-6 flex flex-col gap-4 ${(isAwaiting || isProcessing) ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="border-b border-stone-700 pb-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--color-text-primary)]">Source: {img.document_name}</h3>
                    <span className="text-sm text-[var(--color-text-muted)]">Page {img.page_number} • ID: {img.id}</span>
                </div>

                {/* Edit Form */}
                <div className="space-y-3 flex-grow">
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                            Title <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded p-2 text-[var(--color-text-primary)] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder-stone-600"
                            placeholder={isAwaiting ? "Analysis pending..." : "Descriptive title..."}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            disabled={!isReady}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">Description</label>
                        <textarea
                            ref={descRef}
                            className="w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded p-2 text-[var(--color-text-primary)] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder-stone-600 h-32 resize-none"
                            placeholder={isAwaiting ? "Analysis pending..." : "Detailed description for RAG context..."}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            disabled={!isReady}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-stone-700">
                    <button
                        onClick={() => onReject(img.id)}
                        className="px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded border border-transparent hover:border-red-900/50 transition-colors"
                        disabled={submitting || !isReady}
                    >
                        ❌ Reject
                    </button>
                    <button
                        onClick={() => onAnalyze(img.id)}
                        className="px-4 py-2 text-purple-300 hover:text-white bg-purple-900/20 hover:bg-purple-600 rounded border border-purple-800 transition-colors flex items-center gap-2"
                        disabled={submitting || triggering || !isReady}
                        title="Run Vision AI to auto-generate title and description."
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                        Analyze
                    </button>
                    <button
                        onClick={handleEdit}
                        className="px-4 py-2 text-blue-300 hover:text-white bg-blue-900/20 hover:bg-blue-600 rounded border border-blue-800 transition-colors"
                        disabled={submitting || !isReady}
                    >
                        ✏️ Edit
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!title.trim() || submitting || !isReady}
                        className={`px-6 py-2 rounded font-medium shadow-sm flex items-center gap-2 transition-all
                            ${(!title.trim() || submitting || !isReady)
                                ? 'bg-stone-700 cursor-not-allowed text-stone-500'
                                : 'bg-green-700 hover:bg-green-600 text-white shadow-green-900/20'
                            }`}
                    >
                        {submitting ? 'Processing...' : '✅ Confirm & Ingest'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageReviewPanel;
